import { useDisclosure } from '@chakra-ui/react';
import { useRouter } from 'next/navigation';
import { useContext, useEffect, useRef, useState } from 'react';
import ReactGA from 'react-ga4';
import { FormProvider, useForm } from 'react-hook-form';
import { toaster } from '~/components/ui/toaster';

import { useOrderIdInfo } from '~/hooks/resources';
import { usePageError } from '~/hooks/store';

import { ERROR_DESCRIPTIONS } from '@roll-web/constants';
import { PlayGachaFormContainer } from '~/components/select/PlayGachaFormContainer';
import { PAYMENT_METHOD_KV } from '~/lib/payment';
import {
  OnErrorCallback,
  OnSuccessCarrierCallback,
  OnSuccessRedirectCallback,
  buildBuyOrderInput,
  buyOrder,
} from '~/src/services/orders';
import { RootContext } from '~/store';
import {
  CarriersPaymentMethodVariables,
  PaymentMethodVariables,
} from '~/types/payment';
import { ApiPaymentMethod } from '~/types/resources/apiPaymentMethod';
import { DeliveryBundle } from '~/types/resources/deliveryBundle';
import { DeliveryInfo } from '~/types/resources/deliveryInfo';
import type { GachaDetail } from '~/types/resources/gachaDetail';

type Carriers = {
  AccessID: string | null;
  Token: string | null;
};

type Props = {
  gachaDetail: GachaDetail;
  gachaPaymentMethods: ApiPaymentMethod[];
  playAmount: number;
  deliveryBundles: DeliveryBundle[];
  deliveryInfo: DeliveryInfo | null;
  isLoadingDeliveryInfo: boolean;
  canDeliveryInfoEdit: boolean;
  deliveryInfoOnOpen: () => void;
  setUrl: (nextUrl: object) => void;
  setGuideLoginOn: () => void;
};

export type RequestOrderSubmitInput = {
  deliveryInfo: DeliveryInfo | null;
  cardSequence: string | null;
  shouldNotDeliver: boolean;
  gachaDetail: GachaDetail;
  playAmount: number;
  paymentMethod: PaymentMethodVariables;
  carriersPaymentMethod?: CarriersPaymentMethodVariables;
  applePaySession?: ApplePaySession;
  tippingMessage?: string;
  paymentDetailInput: {
    play_ticket_id?: string;
    totalPrice?: string;
  };
};

export const SelectDetail: React.FC<Props> = (props: Props) => {
  const router = useRouter();
  const { token } = useContext(RootContext);
  const { setPageError } = usePageError();
  const [isSubmitting, setSubmitting] = useState(false);
  const [orderId, setOrderId] = useState<string>();
  const { order } = useOrderIdInfo(orderId);
  const gpsCheckinDisclosure = useDisclosure();

  const carrierForm = useForm<Carriers>({
    mode: 'onSubmit',
  });
  const [paymentRedirectUrl, setNavigateUrl] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const playAmounts = props.gachaDetail.play_amounts;

  useEffect(() => {
    if (paymentRedirectUrl) {
      inputRef.current?.click();
      setSubmitting(false);
    }
  }, [paymentRedirectUrl]);

  useEffect(() => {
    switch (order?.status) {
      case 'assigned':
      case 'delivered':
      case 'undelivered':
        router.replace(`/e/${order.gacha.unique_name}/orders/${order.id}/open`);
        break;
      default:
        break;
    }
  }, [order, router]);

  const onSuccessCallback = async (order_id: string) => {
    ReactGA.event({
      category: props.gachaDetail.unique_name,
      action: `buy_${props.playAmount}_complete`,
    });
    router.replace(
      `/e/${props.gachaDetail.unique_name}/orders/${order_id}/open`
    );

    setSubmitting(false);
    toaster.success({
      title: '購入が完了しました',
      duration: 2000,
    });
  };

  const onErrorCallback: OnErrorCallback = async (
    pageError: string,
    errorBody?: string,
    errorTips?: string
  ) => {
    setSubmitting(false);
    setPageError({
      title: pageError,
      body: errorBody ?? '',
      extraText: errorTips ?? '',
    });
  };

  const onSuccessCarriersCallback: OnSuccessCarrierCallback = async (
    order_id: string,
    access_id: string,
    redirect_url: string,
    auToken: string
  ) => {
    setOrderId(order_id);
    carrierForm.setValue('AccessID', encodeURI(access_id));
    carrierForm.setValue('Token', encodeURI(auToken));
    setNavigateUrl(redirect_url);
    setTimeout(() => {
      inputRef.current?.click();
      setSubmitting(false);
    }, 2500);
  };

  const onSuccessRedirectTypePayments: OnSuccessRedirectCallback = async (
    order_id: string,
    redirect_url: string
  ) => {
    setOrderId(order_id);
    router.push(redirect_url);
    setSubmitting(false);
  };

  const handleOrderSubmit = async (input: RequestOrderSubmitInput) => {
    const {
      deliveryInfo,
      cardSequence,
      shouldNotDeliver,
      gachaDetail,
      playAmount,
      paymentMethod,
      tippingMessage,
    } = input;

    if (token == null) {
      setPageError({
        title: ERROR_DESCRIPTIONS.UNAUTHORIZED.title,
        body: ERROR_DESCRIPTIONS.UNAUTHORIZED.description,
      });
      return;
    }

    setSubmitting(true);

    try {
      const buyOrderInput = buildBuyOrderInput({
        paymentMethod,
        gachaDetail,
        playAmount,
        token: token.token,
        deliveryInfo,
        shouldNotDeliver,
        callbacks: {
          onSuccessInstant: onSuccessCallback,
          onSucessRedirect: onSuccessRedirectTypePayments,
          onSucessCarrier: onSuccessCarriersCallback,
          onError: onErrorCallback,
        },
        ...(paymentMethod == PAYMENT_METHOD_KV.credit && {
          cardSequence,
        }),
        ...(paymentMethod == PAYMENT_METHOD_KV.playTicket && {
          playTicketInput: input.paymentDetailInput,
        }),
        ...(paymentMethod == PAYMENT_METHOD_KV.applepay && {
          domainName: window.location.hostname ?? '',
          label: process.env.NEXT_PUBLIC_PROVIDER_INFO,
          toaster,
          applePaySession: input.applePaySession,
        }),
        ...(paymentMethod == PAYMENT_METHOD_KV.googlepay && {
          totalPrice: input.paymentDetailInput.totalPrice,
        }),
        tippingMessage,
      });
      await buyOrder(buyOrderInput);
    } catch (error) {
      onErrorCallback(error);
    }
  };

  return (
    <>
      {!props.isLoadingDeliveryInfo && (
        <PlayGachaFormContainer
          gachaDetail={props.gachaDetail}
          gachaPaymentMethods={props.gachaPaymentMethods}
          playAmount={props.playAmount}
          deliveryBundles={props.deliveryBundles}
          deliveryInfoOnOpen={props.deliveryInfoOnOpen}
          onOrderSubmit={handleOrderSubmit}
          playAmountPlans={playAmounts}
          paymentRedirectUrl={paymentRedirectUrl}
          isSubmitting={isSubmitting}
          canDeliveryInfoEdit={props.canDeliveryInfoEdit}
          gpsCheckinDisclosure={gpsCheckinDisclosure}
        />
      )}
      <FormProvider {...carrierForm}>
        <form
          action={paymentRedirectUrl ?? undefined}
          method="POST"
          noValidate
          encType="application/x-www-form-urlencoded"
        >
          <input {...carrierForm.register('AccessID')} type="hidden" />
          <input {...carrierForm.register('Token')} type="hidden" />
          <input type="submit" ref={inputRef} style={{ display: 'none' }} />
        </form>
      </FormProvider>
    </>
  );
};
